import {Box, debounce, OutlinedInput, Typography} from '@mui/material';
import {useCanAny} from 'src/permissions/hooks';
import {ChangeEvent, FC, useMemo, useState} from 'react';
import {rfidTableSx} from './RfidTable.sx';
import {searchInputSx} from 'src/components/tables/tableToolbar.sx';
import {ResponsiveTable} from '../ResponsiveTable';
import {CardList} from 'src/components/common/CardList';
import {TagOwnerType, TagsDTO} from 'src/types/tags';
import {RfidTableForDedicatedPageProps} from './RfidTable.types';
import {FilterBar, FilterBarField} from '../FilterBar';
import {AMPFormSelect} from '../AMPFormSelect';
import {Table} from 'src/components/tables/Table';
import {TableLayout} from 'src/components/tables/TableLayout';
import {GridColumns, GridEnrichedColDef} from '@mui/x-data-grid';
import {FormattedMessage, useIntl} from 'react-intl';
import {BlockStatusEnum} from '../RfidTable/RfidTable.types';
import dayjs from 'dayjs';
import {useBusinessId, useAppSelector} from 'src/app/hooks';
import {RfidActionsForDedicatedPage} from '../RfidTable/RfidActionsForDedicatedPage';
import {ColorField} from '../ColorField';
import {getUserDisplayName} from 'src/utils/userNameFormatter';
import {tagOwnerOf} from 'src/utils/tagOwner';
import {RfidItemCardForDedicatedPage} from './RfidItemCardForDedicatedPage';
import {Link} from 'react-router-dom';
import {EmployeeCell} from 'src/components/users/EmployeeCell';
import {UserDTO} from 'src/types';
import {AppRouteEnum, AppRouteSpace} from 'src/utils/helper';
import {Search} from '@mui/icons-material';

interface RfidTableColumnsProps {
    /**
     * Passed in rather than read here: `columns` is a plain function, not a component, so it cannot
     * call a hook. It used to consult the CASL ability singleton directly, which worked only because
     * that singleton is module state - the one mechanism that does not care where it is called from,
     * and the reason this check drifted out of sight.
     */
    canManageRfid: boolean;
    isSuperAdmin: boolean;
    isBusinessUser: boolean;
    isCompanyUser: boolean;
    isEmployeeUser: boolean;
    holderView: boolean;
    businessId: number;
}

const ALL = '';
const ACTIVE = 'ACTIVE';
const BLOCKED = 'BLOCKED';

const columns = ({
                     isSuperAdmin,
                     isBusinessUser,
                     isCompanyUser,
                     isEmployeeUser,
                     holderView,
                     businessId,
                     canManageRfid,
                 }: RfidTableColumnsProps): GridColumns => {
    const resultArray: GridColumns = [];

    const idTagsColumn: GridEnrichedColDef = {
        field: 'idTag',
        renderHeader: () => (
            <Typography sx={rfidTableSx.colTitle}>
                <FormattedMessage id={'rfidTag'}/>
            </Typography>
        ),
        minWidth: 50,
        flex: 1,
        sortable: true,
        renderCell: (params) => (
            <Link to={`/app/rfids/${params?.row?.id}`}>
                <Typography>{params?.row?.idTag ?? ''}</Typography>
            </Link>
        ),
    };

    const licensePlateColumn: GridEnrichedColDef = {
        field: 'licensePlate',
        renderHeader: () => (
            <Typography sx={rfidTableSx.colTitle}>
                <FormattedMessage id={'licensePlate'}/>
            </Typography>
        ),
        minWidth: 50,
        flex: 1,
        sortable: false,
        renderCell: (params) => (
            <Typography>{params?.row?.licensePlate || '—'}</Typography>
        ),
    };

    const employeeColumn: GridEnrichedColDef = {
        field: 'employee.firstName',
        renderHeader: () => (
            <Typography sx={rfidTableSx.colTitle}>
                <FormattedMessage id={'employee'}/>
            </Typography>
        ),
        minWidth: 50,
        flex: 1,
        sortable: false,
        renderCell: (params) => {
            const data = params?.row as TagsDTO;

            if (data?.employee?.firstName && data?.employee?.lastName) {
                const employeeData = {
                    ...data.employee,
                    bexioDataList: data.employee?.user?.bexioDataList,
                    employee: {
                        ...data.employee,
                        company: {id: data?.company?.id ?? 0, name: '', createdAt: 0, billingEmail: ''}
                    },
                    business: {
                        id: data?.business?.id ?? 0
                    }
                };
                return (
                    <EmployeeCell data={employeeData as unknown as UserDTO} isEmployeeUser={isEmployeeUser}/>
                );
            }

            return <Typography color="primary.dark">-</Typography>;
        },
    };

    const statusColumn: GridEnrichedColDef = {
        field: 'blocked',
        renderHeader: () => (
            <Typography sx={rfidTableSx.colTitle}>
                <FormattedMessage id={'statusColumnHeader'}/>
            </Typography>
        ),
        minWidth: 50,
        flex: 1,
        sortable: false,
        renderCell: (params) => (
            <Box
                sx={[
                    rfidTableSx.tag,
                    !params?.row?.blocked && rfidTableSx.tagActive,
                    params?.row?.blocked && rfidTableSx.tagBlocked,
                ]}
            >
                <FormattedMessage
                    id={params.row?.blocked ? BlockStatusEnum.blocked : BlockStatusEnum.accepted}
                />
            </Box>
        ),
    };

    const addedOnColumn: GridEnrichedColDef = {
        field: 'created',
        renderHeader: () => (
            <Typography sx={rfidTableSx.colTitle}>
                <FormattedMessage id={'rfidTagAddedOnTableHeader'}/>
            </Typography>
        ),
        minWidth: 50,
        flex: 1,
        sortable: false,
        renderCell: (params) => (
            <Typography>
                {params?.row?.created && dayjs(params?.row?.created).format('DD/MM/YYYY')}
            </Typography>
        ),
    };

    const clientsRoleColumn: GridEnrichedColDef = {
        field: 'role',
        renderHeader: () => (
            <Typography sx={rfidTableSx.colTitle}>
                <FormattedMessage id={'clientsRoleTableHeader'}/>
            </Typography>
        ),
        minWidth: 50,
        flex: 1,
        sortable: false,
        renderCell: (params) => {
            // Whose tag this is - a driver's, a company's, an employee's, the staff's. That is the
            // holder's account type, not their roles: a single cell cannot show several, which is how
            // you can tell. A manager's own card used to read here as a private driver's.
            const owner = tagOwnerOf(params?.row as TagsDTO);

            if (!owner?.type) {
                return <Typography>-</Typography>;
            }

            return <ColorField label={owner.type}/>;
        },
    };

    const nameColumn: GridEnrichedColDef = {
        field: 'name',
        renderHeader: () => (
            <Typography sx={rfidTableSx.colTitle}>
                <FormattedMessage id="nameColumnHeader"/>
            </Typography>
        ),
        minWidth: 50,
        flex: 1,
        sortable: false,
        renderCell: (params) => {
            const data = params?.row as TagsDTO;

            // No business name here any more. The list is one business's tags - it says so in the
            // sidebar - so this column repeated that name on every row instead of saying whose tag
            // it is, which is the one thing the column is for.

            // A card handed to a person - a private driver, or staff with a card of their own. The name
            // goes through the shared formatter: a company's login has no first or last name.
            if (data?.users?.length > 0) {
                const driver = data.users[0];
                const driverLink = AppRouteSpace([AppRouteEnum.app, AppRouteEnum.users, String(driver.id)]);

                return (
                    <Link to={driverLink}>
                        <Typography color="primary.main">
                            {getUserDisplayName(driver) || '-'}
                        </Typography>
                    </Link>
                );
            }

            // Check if tag belongs to a company
            if (data?.company?.name) {
                const companyLink = AppRouteSpace([AppRouteEnum.app, AppRouteEnum.users, String(data.company.user.id)]);

                return (
                    <Link to={companyLink}>
                        <Typography color="primary.main">
                            {data.company.name}
                        </Typography>
                    </Link>
                );
            }

            // A master card, held by the member of staff it was issued to.
            if (data?.manager) {
                const managerLink = AppRouteSpace([AppRouteEnum.app, AppRouteEnum.users, String(data.manager.id)]);

                return (
                    <Link to={managerLink}>
                        <Typography color="primary.main">
                            {getUserDisplayName(data.manager) || '-'}
                        </Typography>
                    </Link>
                );
            }

            return <Typography>-</Typography>;
        },
    };

    const actionsColumn: GridEnrichedColDef = {
        field: 'actions',
        headerName: '',
        width: 120,
        sortable: false,
        cellClassName: 'ampTableCell',
        align: 'right' as const,
        headerAlign: 'right' as const,
        renderCell: (params) => {
            return (
                <RfidActionsForDedicatedPage
                    businessId={businessId ?? params?.row?.business?.id}
                    blocked={params?.row?.blocked}
                    ocppTagId={params?.row?.id}
                    idTabObject={params?.row as TagsDTO}
                />
            );
        },
    };

    // The holder's own cards. Whose card it is goes unsaid - the reader holds it - and the status reads
    // as working or switched off, the only two states a holder can tell apart or act on.
    if (holderView) {
        const holderStatusColumn: GridEnrichedColDef = {
            ...statusColumn,
            renderCell: (params) => (
                <Box
                    sx={[
                        rfidTableSx.tag,
                        !params?.row?.blocked && rfidTableSx.tagActive,
                        params?.row?.blocked && rfidTableSx.tagBlocked,
                    ]}
                >
                    <FormattedMessage id={params.row?.blocked ? 'rfidDeactivated' : 'active'}/>
                </Box>
            ),
        };

        // What a holder can do about a card lives in the strip above the list, not on every row.
        return [idTagsColumn, licensePlateColumn, holderStatusColumn, addedOnColumn];
    }

    resultArray.push(idTagsColumn);

    if (isCompanyUser || isEmployeeUser) {
        resultArray.push(employeeColumn, licensePlateColumn, statusColumn, addedOnColumn);
    }

    if (isBusinessUser) {
        resultArray.push(clientsRoleColumn, nameColumn, employeeColumn, licensePlateColumn, statusColumn);
    }



    // A private driver matched none of the branches above and was left with one column: the tag, and
    // not even whether it works. These three are everything a driver's own card can say that they do
    // not already know - which car it is for, whether it is blocked, and when it was issued. The
    // owner and the employee are left out because on this page the owner is the reader.
    //
    // Written as a fallback rather than as a fifth flag, so that a persona added later cannot end up
    // with a bare list again by simply not being mentioned here.
    if (!isCompanyUser && !isEmployeeUser && !isBusinessUser && !isSuperAdmin) {
        resultArray.push(licensePlateColumn, statusColumn, addedOnColumn);
    }

    if (canManageRfid) {
        resultArray.push(actionsColumn);
    }

    return resultArray;
};

export const RfidTableForDedicatedPage: FC<RfidTableForDedicatedPageProps> = ({
                                                                                  data,
                                                                                  handleSort,
                                                                                  handleChangePage,
                                                                                  handleChangeRowsNumber,
                                                                                  handleSearch,
                                                                                  handleFilter,
                                                                                  isSuperAdmin,
                                                                                  isBusinessUser,
                                                                                  isCompanyUser,
                                                                                  isEmployeeUser,
                                                                                  holderView = false,
                                                                                  filters,
                                                                                  searchValue,
                                                                                  title,
                                                                                  onAddClick,
                                                                                  buttonText,
                                                                                  onlyTitle,
                                                                                  icon,
                                                                                  extraHeaderAction,
                                                                              }) => {
    const canManageRfid = useCanAny('rfidTags.update', 'rfidTags.create', 'rfidTags.delete');
    const intl = useIntl();

    const {authInfo} = useAppSelector((state) => state.common);
    const businessId = useBusinessId();

    const [ownBlocked, setBlocked] = useState<string>(ALL);
    const [ownOwnerType, setOwnerType] = useState<string>(ALL);
    // Controlled when the page hands its filters down: the strip above sets the same filters, and a
    // dropdown keeping its own copy went on saying "all" while the list under it was narrowed.
    const blocked = filters
        ? (filters.blocked === undefined ? ALL : filters.blocked ? BLOCKED : ACTIVE)
        : ownBlocked;
    const ownerType = filters ? (filters.ownerType ?? ALL) : ownOwnerType;

    // Whose card it is only asks something on a list that holds more than one kind. A company sees
    // its own cards, and an employee or a driver sees theirs, so the question has one answer there
    // before it is asked; offering it would be a filter that can only empty the list.
    const showOwnerType = !isCompanyUser && !isEmployeeUser && !holderView;

    const apply = (next: {blocked?: string; ownerType?: string}) => {
        const value = {blocked, ownerType, ...next};
        setBlocked(value.blocked);
        setOwnerType(value.ownerType);
        handleFilter?.({
            // Neither, not "active": the list's default shows blocked cards too.
            blocked: value.blocked === ALL ? undefined : value.blocked === BLOCKED,
            ownerType: (value.ownerType || undefined) as TagOwnerType | undefined,
        });
    };

    const statusOptions = useMemo(() => [
        {id: 0, name: intl.formatMessage({id: 'allStatuses'}), value: ALL},
        {id: 1, name: intl.formatMessage({id: 'active'}), value: ACTIVE},
        {id: 2, name: intl.formatMessage({id: 'blocked'}), value: BLOCKED},
    ], [intl]);

    const ownerOptions = useMemo(() => [
        {id: 0, name: intl.formatMessage({id: 'allOwnerTypes'}), value: ALL},
        {id: 1, name: intl.formatMessage({id: 'privateUserTabName'}), value: 'DRIVER'},
        {id: 2, name: intl.formatMessage({id: 'companyTabForRfidDialog'}), value: 'COMPANY'},
        {id: 3, name: intl.formatMessage({id: 'employee'}), value: 'EMPLOYEE'},
        {id: 4, name: intl.formatMessage({id: 'managerTabForRfidDialog'}), value: 'MANAGER'},
        {id: 5, name: intl.formatMessage({id: 'unassigned'}), value: 'UNASSIGNED'},
    ], [intl]);

    const fields: FilterBarField[] = handleFilter
        ? [
            {
                id: 'status',
                control: (
                    <AMPFormSelect
                        dense
                        data={statusOptions}
                        value={blocked}
                        input="status"
                        onChange={(e) => apply({blocked: String(e.target.value)})}
                    />
                ),
            },
            ...(showOwnerType ? [{
                id: 'ownerType',
                wide: true,
                control: (
                    <AMPFormSelect
                        dense
                        data={ownerOptions}
                        value={ownerType}
                        input="ownerType"
                        onChange={(e) => apply({ownerType: String(e.target.value)})}
                    />
                ),
            }] : []),
        ]
        : [];

    const handleSearchFieldChange = (
        event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
    ): void => {
        if (!handleSearch) {
            return;
        }

        debounce(() => handleSearch(event.target.value), 1000)();
    };

    return (
        <Box>
            <TableLayout
                toolbar={(
                    <FilterBar
                        title={title}
                        icon={icon}
                        onAdd={onlyTitle ? undefined : onAddClick}
                        addLabel={buttonText}
                        subtitle={intl.formatMessage({id: 'rowsFound'}, {count: data.page.totalElements})}
                        fields={fields}
                        hasActiveFilters={Boolean(blocked || ownerType)}
                        onClearAll={handleFilter ? () => apply({blocked: ALL, ownerType: ALL}) : undefined}
                        actions={(
                            <>
                                <OutlinedInput
                                    sx={searchInputSx}
                                    color="secondary"
                                    type="text"
                                    placeholder={intl.formatMessage({id: 'search'})}
                                    name="search"
                                    // Uncontrolled, so the debounce is not fighting a re-render on
                                    // every keystroke - but seeded, so the box still says what the
                                    // list is filtered by after a Back.
                                    defaultValue={searchValue ?? ''}
                                    onChange={handleSearchFieldChange}
                                    startAdornment={<Search/>}
                                />
                                {extraHeaderAction}
                            </>
                        )}
                    />
                )}
                totalPages={data.page.totalPages}
                page={data.page.number}
                pageSize={data?.page?.size ?? 10}
                onPageChange={handleChangePage}
                onPageSizeChange={(event: ChangeEvent<HTMLInputElement>) =>
                    handleChangeRowsNumber(+event.target.value)
                }
            >
            <ResponsiveTable
                desktopElement={
                    <Table
                        sx={rfidTableSx.table}
                        rows={data?.content?.map((item, index) => ({...item, _uniqueIndex: index})) ?? []}
                        hideFooter
                        getRowId={(row) => `${row?.id}-${row?.idTag}-${row?.created}-${row._uniqueIndex}`}
                        columns={columns({
                            isSuperAdmin,
                            isBusinessUser,
                            isCompanyUser,
                            isEmployeeUser,
                            holderView,
                            businessId,
                            canManageRfid,
                        })}
                        handleSort={handleSort}
                    />
                }
                responsiveElement={
                    <CardList>
                        {data?.content?.map((item, index) => (
                            <RfidItemCardForDedicatedPage
                                key={`${item?.id}-${item?.idTag}-${item?.created}-${index}`}
                                tag={item}
                                businessId={businessId}
                                holderView={holderView}
                            />
                        ))}
                    </CardList>
                }
            />
            </TableLayout>
        </Box>
    );
};
